import type { ComponentType } from "react";
import type { Instrument, InstrumentSlot } from "../../../../../src/lib/audio";
import { projectStore } from "../../store/projectStore";
import { DrumEditor } from "./DrumEditor";
import { ExternalMidiEditor } from "./ExternalMidiEditor";
import { SamplerEditor } from "./SamplerEditor";
import { Sf2Editor } from "./Sf2Editor";
import { SynthEditor } from "./SynthEditor";
import { WavetableEditor } from "./WavetableEditor";

/** Instrument kinds the left dock can host. Extend this list when adding kinds. */
export type InstrumentKind = Instrument["kind"];

export type InstrumentEditorProps = {
  slot: InstrumentSlot;
};

export type InstrumentKindOption = {
  kind: InstrumentKind;
  label: string;
  /** Short badge on slot tabs. */
  short: string;
};

/** Ordered picker options — add new kinds here (and in `instrumentEditors`). */
export const INSTRUMENT_KIND_OPTIONS: readonly InstrumentKindOption[] = [
  { kind: "synth", label: "Synth", short: "S" },
  { kind: "wavetable", label: "Wavetable", short: "W" },
  { kind: "sampler", label: "Sampler", short: "A" },
  { kind: "drums", label: "Drums", short: "D" },
  { kind: "sf2", label: "SoundFont", short: "F" },
  { kind: "externalMidi", label: "External MIDI", short: "M" },
] as const;

/**
 * Body registry for the slot inspector. Shell picks `editors[kind]`.
 * Every `InstrumentKind` must have an entry.
 */
export const instrumentEditors: Record<
  InstrumentKind,
  ComponentType<InstrumentEditorProps>
> = {
  synth: SynthEditor,
  wavetable: WavetableEditor,
  sampler: SamplerEditor,
  drums: DrumEditor,
  sf2: Sf2Editor,
  externalMidi: ExternalMidiEditor,
};

export function isInstrumentKind(value: unknown): value is InstrumentKind {
  return typeof value === "string" && Object.hasOwn(instrumentEditors, value);
}

/** Resolve a stored kind to a registered dock kind (unknown → synth fallback). */
export function resolveInstrumentKind(kind: unknown): InstrumentKind {
  return isInstrumentKind(kind) ? kind : "synth";
}

export function instrumentKindLabel(kind: InstrumentKind): string {
  return INSTRUMENT_KIND_OPTIONS.find(option => option.kind === kind)?.label ?? kind;
}

export function instrumentKindShort(kind: InstrumentKind): string {
  return INSTRUMENT_KIND_OPTIONS.find(option => option.kind === kind)?.short ?? "?";
}

/** Read slot kind from the live project store. */
export function instrumentKindForSlot(slot: InstrumentSlot): InstrumentKind {
  return resolveInstrumentKind(projectStore.getValue().instruments[slot]?.kind);
}
